/**
 * ApplyRateSetDialog — pre-fill a client's rates from one of the
 * tenant's standard rate sets (tenants.config billing.rate_sets).
 *
 * Opened from the Rates card of a client with no active rates. The
 * person picks a set and a start date (defaulting to the page-injected
 * today), sees exactly which bands the set will write at which amounts
 * as labelled lines, and saves. "Apply rate set" is disabled only while
 * sending: pressed with no set or no real date it sends nothing, shows
 * each gap's sentence under its box and moves focus to the first. The
 * server writes one explicit client_rates row per band in the set
 * through the ordinary rates write path, with the trail a typed rate
 * gets, and echoes the refreshed history for the card.
 *
 * A band the set leaves blank is untouched: it stays "No rate on file"
 * for this client, and that is a fact, not a problem.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Field } from '@/components/primitives/Field';
import type { RateBandOption } from '@/lib/notes';
import { formatMoney, type ClientRateRowWire } from '@/lib/clientRates';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  applyRateSetErrorMessage,
  postApplyRateSet,
  rateSetBandLines,
  type ApplyRateSetResponse,
  type RateSetWire,
} from '@/lib/rateSets';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

export interface ApplyRateSetDialogProps {
  clientId: string;
  /** The tenant's sets (readRateSets over tenant config; non-empty). */
  sets: readonly RateSetWire[];
  /** The priceable vocabulary, for labels and order. */
  bands: readonly RateBandOption[];
  today: string;
  /** Receives the echoed refreshed history. */
  onApplied: (rates: ClientRateRowWire[]) => void;
  onClose: () => void;
}

export function ApplyRateSetDialog({
  clientId,
  sets,
  bands,
  today,
  onApplied,
  onClose,
}: ApplyRateSetDialogProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [setId, setSetId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();
  const [setGap, setSetGap] = useState(false);
  const [dateGap, setDateGap] = useState(false);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const dateRef = useRef<HTMLInputElement | null>(null);

  const chosen = sets.find((s) => s.id === setId) ?? null;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom);

  // The request is built where Save is pressed and travels as the
  // mutation's variables: React Query hands a mutation its options in an
  // effect, so a mutationFn reading this render's values could send an
  // earlier render's.
  const applyMut = useMutation<ApplyRateSetResponse, unknown, { rate_set_id: string; effective_from: string }>({
    mutationFn: (body) => postApplyRateSet(tenantSlug, clientId, body),
    onSuccess: (res) => {
      onApplied(res.rates);
      onClose();
    },
    onError: (err) => setError(applyRateSetErrorMessage(err, contact)),
  });

  function pressApply(): void {
    if (chosen === null || !dateOk) {
      setSetGap(chosen === null);
      setDateGap(!dateOk);
      (chosen === null ? selectRef : dateRef).current?.focus();
      return;
    }
    applyMut.mutate({ rate_set_id: setId, effective_from: effectiveFrom });
  }

  return (
    <Dialog
      title="Apply a rate set"
      size="sm"
      testId="apply-rate-set-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={applyMut.isPending}
            loading={applyMut.isPending}
            data-testid="apply-rate-set-save"
            onClick={pressApply}
          >
            Apply rate set
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2 py-2">
        <p className="text-xs text-ink-soft">
          Sets a rate for each kind of work in the set. Kinds of work the set leaves blank stay unset.
        </p>
        <Field
          label="Rate set"
          required
          htmlFor="apply-rate-set-select"
          error={setGap ? 'Choose a rate set.' : undefined}
        >
          <SelectField size="compact"
            ref={selectRef}
            id="apply-rate-set-select"
            data-testid="apply-rate-set-select"
            className="w-full"
            value={setId}
            aria-required="true"
            aria-invalid={setGap ? 'true' : undefined}
            aria-describedby={setGap ? 'apply-rate-set-select-error' : undefined}
            onChange={(e) => {
              setError(null);
              setSetGap(false);
              setSetId(e.target.value);
            }}
          >
            <option value="">Choose a set</option>
            {sets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field
          label="Starts on"
          required
          htmlFor="apply-rate-set-from"
          error={dateGap ? 'Choose the date these rates start.' : undefined}
        >
          <DateInput
            ref={dateRef}
            id="apply-rate-set-from"
            data-testid="apply-rate-set-from"
            size="field" className="w-full max-w-[11rem]"
            value={effectiveFrom}
            aria-required="true"
            aria-invalid={dateGap ? 'true' : undefined}
            aria-describedby={dateGap ? 'apply-rate-set-from-error' : undefined}
            onChange={(e) => {
              setError(null);
              setDateGap(false);
              setEffectiveFrom(e.target.value);
            }}
          />
        </Field>
        {chosen !== null && (
          <dl data-testid="apply-rate-set-lines" className="mt-1 grid gap-1">
            {rateSetBandLines(chosen, bands).map((line) => (
              <div key={line.band} className="flex flex-wrap items-baseline gap-x-3">
                <dt className="shrink-0 min-w-[9.5em] text-label tracking-label font-semibold uppercase text-sage-400">
                  {line.label}
                </dt>
                <dd className="m-0 min-w-0 flex-1 basis-[9em] text-value font-medium text-sage-800 [overflow-wrap:anywhere]">
                  {formatMoney(line.rate)} / hour
                </dd>
              </div>
            ))}
          </dl>
        )}
        {error !== null && (
          <p role="alert" data-testid="apply-rate-set-error" className="text-xs text-pepper">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
